import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import getRestaurant from '@menu/Utils/getRestaurant';
import { ProductResource } from '@menu/Types/Resources/ProductResource';

export const useFavoriteProductsStore = defineStore('favoriteProducts', () => {
  const favoriteProductIds = ref<number[]>(
    (() => {
      try {
        // it'll throw an exception if the structure is invalid
        return [
          ...JSON.parse(localStorage.getItem('favoriteProductIds') ?? '[]'),
        ].filter((favoriteProductId) => typeof favoriteProductId === 'number');
      } catch (e) {
        // because sometimes `SecurityError: The operation is insecure.` happens
        // ...or because data is invalid for whatever reason
        return [];
      }
    })(),
  );

  watch(favoriteProductIds, () => {
    try {
      localStorage.setItem(
        'favoriteProductIds',
        JSON.stringify(favoriteProductIds.value),
      );
    } catch (e) {
      // because sometimes `SecurityError: The operation is insecure.` happens
    }
  });

  const restaurant = getRestaurant();

  const products = computed(() => {
    const products: ProductResource[] = [];

    restaurant.menus.forEach((menu) => {
      menu.categories.forEach((category) => {
        category.products.forEach((product) => {
          products.push(product);
        });
      });
    });

    return products;
  });

  const favoriteProducts = computed(() => {
    const favoriteProducts: ProductResource[] = [];

    favoriteProductIds.value.forEach((id) => {
      const favoriteProduct = products.value.find(
        (product) => product.id === id,
      );

      if (favoriteProduct) {
        favoriteProducts.push(favoriteProduct);
      }
    });

    return favoriteProducts;
  });

  const addToFavorites = (productId: number) => {
    if (favoriteProductIds.value.includes(productId)) {
      return;
    }

    favoriteProductIds.value = [...favoriteProductIds.value, productId];
  };

  const removeFromFavorites = (productId: number) => {
    favoriteProductIds.value = favoriteProductIds.value.filter(
      (id) => id !== productId,
    );
  };

  const isFavorite = (productId: number) => {
    return favoriteProductIds.value.includes(productId);
  };

  return {
    favoriteProducts,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
  };
});
